import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiShield, FiSmartphone, FiQrCode } = FiIcons;

const FinalCTA = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-orange-900 to-red-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black/40"></div>
        <img
          src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          alt="Transformed lives smiling"
          className="w-full h-full object-cover opacity-30"
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
      <div className="absolute top-40 right-20 w-3 h-3 bg-orange-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-40 left-20 w-2 h-2 bg-red-400 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Quote */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="text-6xl md:text-8xl text-yellow-400 mb-4 font-serif">"</div>
            <blockquote className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              A coin lasts a day.
              <span className="block text-yellow-400">Dignity lasts a lifetime.</span>
            </blockquote>
            <div className="text-xl text-gray-300 font-medium">
              - Begging Free India Foundation
            </div>
          </motion.div>

          {/* Main CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Be the Change.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Start Today.
              </span>
            </h2>

            <div className="flex flex-col lg:flex-row gap-8 items-center justify-center max-w-4xl mx-auto">
              {/* Donation Buttons */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255, 193, 7, 0.4)' }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-yellow-500 text-gray-900 py-4 px-6 rounded-2xl font-bold text-lg hover:bg-yellow-400 transition-all duration-300 shadow-2xl"
                  >
                    ₹500
                    <div className="text-sm opacity-80">One Day of Dignity</div>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(251, 146, 60, 0.4)' }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-orange-500 text-white py-4 px-6 rounded-2xl font-bold text-lg hover:bg-orange-400 transition-all duration-300 shadow-2xl"
                  >
                    ₹5,000
                    <div className="text-sm opacity-80">Transform 10 Lives</div>
                  </motion.button>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(239, 68, 68, 0.4)' }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white py-6 px-8 rounded-2xl font-bold text-xl hover:from-red-700 hover:to-red-600 transition-all duration-300 shadow-2xl"
                >
                  💝 Donate Custom Amount - Be the Change
                </motion.button>
              </div>

              {/* QR Code Section */}
              <div className="flex-shrink-0">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 text-center shadow-2xl"
                >
                  <div className="w-32 h-32 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiQrCode} className="w-16 h-16 text-gray-600" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900 mb-2">Quick Donate</div>
                  <div className="text-xs text-gray-600">Scan to donate instantly</div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-8 text-white/80"
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiShield} className="w-5 h-5 text-green-400" />
              <span className="font-medium">Secure Payment</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiHeart} className="w-5 h-5 text-red-400" />
              <span className="font-medium">80G Tax Benefits</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiSmartphone} className="w-5 h-5 text-blue-400" />
              <span className="font-medium">Instant Receipt</span>
            </div>
          </motion.div>

          {/* Bottom Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-4xl mx-auto"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">2,547+</div>
              <div className="text-white/80">Lives Already Transformed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">95%</div>
              <div className="text-white/80">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-orange-400 mb-2">1,247</div>
              <div className="text-white/80">Active Supporters</div>
            </div>
          </motion.div>

          {/* Final Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Your contribution today doesn't just help someone survive - it helps them thrive. 
              <strong className="text-white"> Join our movement for a dignified, begging-free India.</strong>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;